import {
  type EndpointType,
  type CodeLanguage,
  type EndpointConfig,
} from "./Endpoints";
import { InferenceConfig } from "../types/demo";
import { DpInferenceConfig } from "../types/dpInferenceConfig";

// 스니펫 생성 함수 타입 정의
type SnippetGenerator = (
  config: Pick<EndpointConfig, "apiUrl" | "defaultModelName">, // 필요한 설정만 받도록 타입 지정
  apiKeyVar: string,
  filePathVar: string,
  inferenceConfig: InferenceConfig,
  modelName?: string,
) => string;

// Placeholders
export const API_KEY_VAR_PLACEHOLDER = "YOUR_UPSTAGE_API_KEY";
export const FILE_PATH_VAR_PLACEHOLDER = "YOUR_FILE_NAME";

// 엔드포인트별/언어별 스니펫 생성 함수 맵
export const ENDPOINT_SNIPPETS: Partial<
  Record<EndpointType, Partial<Record<CodeLanguage, SnippetGenerator>>>
> = {
  //
  //
  // DP
  //
  //
  "layout-analysis": {
    python: (
      config,
      apiKeyVar,
      filePathVar,
      inferenceConfig,
    ) => `# pip install requests
 
import requests
 
api_key = "${apiKeyVar}"  # ex: up_xxxYYYzzzAAAbbbCCC
file_path = "${filePathVar}"  # ex: ./document.pdf
 
url = "${config.apiUrl}"
headers = {"Authorization": f"Bearer {api_key}"}
files = {"document": open(file_path, "rb")}
data = {
    "model": "${(inferenceConfig as DpInferenceConfig).model}",
    "ocr": "${(inferenceConfig as DpInferenceConfig).ocr}",
    "chart_recognition": ${
      (inferenceConfig as DpInferenceConfig).chartRecognition ? "True" : "False"
    },
    "coordinates": ${
      (inferenceConfig as DpInferenceConfig).coordinates ? "True" : "False"
    },
    "output_formats": '${JSON.stringify(
      (inferenceConfig as DpInferenceConfig).outputFormats,
    )}',
    "base64_encoding": '${JSON.stringify(
      (inferenceConfig as DpInferenceConfig).base64Encoding,
    )}',
}

response = requests.post(url, headers=headers, files=files, data=data)
 
print(response.json())
`,
    langchain: (
      config,
      apiKeyVar,
      filePathVar,
      inferenceConfig,
    ) => `# pip install -qU langchain-core langchain-upstage
 
import os
from langchain_upstage import UpstageDocumentParseLoader
 
os.environ["UPSTAGE_API_KEY"] = "${apiKeyVar}" # ex: up_xxxYYYzzzAAAbbbCCC
file_path = "${filePathVar}" # ex: ./document.pdf

loader = UpstageDocumentParseLoader(
    file_path,
    model="${(inferenceConfig as DpInferenceConfig).model}",
    ocr="${(inferenceConfig as DpInferenceConfig).ocr}",
    coordinates=${
      (inferenceConfig as DpInferenceConfig).coordinates ? "True" : "False"
    },
    base64_encoding=${JSON.stringify(
      (inferenceConfig as DpInferenceConfig).base64Encoding,
    )},
) 
pages = loader.load()  # or loader.lazy_load()
for page in pages:
    print(page)
`,
    curl: (config, apiKeyVar, filePathVar, inferenceConfig) => `#bin/bash
    
UPSTAGE_API_KEY="${apiKeyVar}"
FILE_PATH="${filePathVar}"

curl --location --request POST '${config.apiUrl}' \\
  --header "Authorization: Bearer \${UPSTAGE_API_KEY}" \\
  --form "document=@\${FILE_PATH}" \\
  --form 'model=${inferenceConfig.model}' \\
  --form 'ocr=${(inferenceConfig as DpInferenceConfig).ocr}' \\
  --form 'chart_recognition=${(inferenceConfig as DpInferenceConfig).chartRecognition}' \\
  --form 'output_formats=${JSON.stringify(
    (inferenceConfig as DpInferenceConfig).outputFormats,
  )}' \\
  --form 'base64_encoding=${JSON.stringify(
    (inferenceConfig as DpInferenceConfig).base64Encoding,
  )}' \\
  --form 'coordinates=${(inferenceConfig as DpInferenceConfig).coordinates}'
`,
    nodejs: (
      config,
      apiKeyVar,
      filePathVar,
      inferenceConfig,
    ) => `// npm install node-fetch form-data
    
import fs from "fs";
import fetch from "node-fetch";
import FormData from "form-data";

const API_KEY = "${apiKeyVar}"; // ex: up_xxxYYYzzzAAAbbbCCC
const FILE_PATH = "${filePathVar}"; // ex: ./document.pdf

async function performLayoutAnalysis() {
  const formData = new FormData();
  formData.append("document", fs.createReadStream(FILE_PATH));
  formData.append("model", "${(inferenceConfig as DpInferenceConfig).model}");
  formData.append("ocr", "${(inferenceConfig as DpInferenceConfig).ocr}");
  formData.append("chart_recognition", ${
    (inferenceConfig as DpInferenceConfig).chartRecognition
      ? '"true"'
      : '"false"'
  });
  formData.append("coordinates", ${(inferenceConfig as DpInferenceConfig).coordinates ? '"true"' : '"false"'});
  formData.append("base64_encoding", JSON.stringify(${JSON.stringify(
    (inferenceConfig as DpInferenceConfig).base64Encoding,
  )}));
  
  try {
    const response = await fetch(
      "${config.apiUrl}", 
      {
        method: "POST",
        headers: { Authorization: \`Bearer \${API_KEY}\` },
        body: formData,
      }
    );
    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }
    console.log(await response.json());
  } catch (error) {
    console.error("Error:", error);
  }
}

performLayoutAnalysis();

`,
  },

  //
  //
  // OCR
  //
  //
  ocr: {
    python: (config, apiKeyVar, filePathVar) => `# pip install requests

import requests

api_key = "${apiKeyVar}" # ex: up_xxxYYYzzzAAAbbbCCC
file_path = "${filePathVar}" # ex: ./image.png

url = "${config.apiUrl}"
headers = {"Authorization": f"Bearer {api_key}"}

files = {"document": open(file_path, "rb")}
data = {"model": "${config.defaultModelName}"}
response = requests.post(url, headers=headers, files=files, data=data)

print(response.json())
`,
    curl: (config, apiKeyVar, filePathVar) => `#bin/bash

UPSTAGE_API_KEY="${apiKeyVar}"
FILE_PATH="${filePathVar}"

curl --location --request POST '${config.apiUrl}' \\
  --header "Authorization: Bearer \${UPSTAGE_API_KEY}" \\
  --form "document=@\${FILE_PATH}" \\
  --form "model=${config.defaultModelName}"
`,
    nodejs: (
      config,
      apiKeyVar,
      filePathVar,
    ) => `// npm install node-fetch form-data

import fs from "fs";
import fetch from "node-fetch";
import FormData from "form-data";

const API_KEY = "${apiKeyVar}"; // ex: up_xxxYYYzzzAAAbbbCCC
const FILE_PATH = "${filePathVar}"; // ex: ./image.png

async function performOCR() {
  const formData = new FormData();
  formData.append("document", fs.createReadStream(FILE_PATH));
  formData.append("schema", "oac");
  formData.append("model", "${config.defaultModelName}");

  try {
    const response = await fetch(
      "${config.apiUrl}", 
      {
        method: "POST",
        headers: { Authorization: \`Bearer \${API_KEY}\` },
        body: formData,
      }
    );
    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }
    console.log(await response.json());
  } catch (error) {
    console.error("Error:", error);
  }
}

performOCR();
`,
  },

  //
  //
  // Prebuilt Information Extraction
  //
  //
  extractor: {
    python: (
      config,
      apiKeyVar,
      filePathVar,
      inferenceConfig,
      modelName,
    ) => `# pip install requests
import requests

api_key = "${apiKeyVar}" # ex: up_xxxYYYzzzAAAbbbCCC
file_path = "${filePathVar}" # ex: ./image.png
model_name = "${modelName || config.defaultModelName}"

url = f"${config.apiUrl}"
headers = {"Authorization": f"Bearer {api_key}"}
files = {"document": open(file_path, "rb")}
data = {"model": model_name}

response = requests.post(url, headers=headers, files=files, data=data)
print(response.json())
`,
    curl: (
      config,
      apiKeyVar,
      filePathVar,
      inferenceConfig,
      modelName,
    ) => `#bin/bash
UPSTAGE_API_KEY="${apiKeyVar}"
FILE_PATH="${filePathVar}"
MODEL_NAME="${modelName || config.defaultModelName}"

curl --location --request POST '${config.apiUrl}' \\
  --header "Authorization: Bearer \${UPSTAGE_API_KEY}" \\
  --form "document=@\${FILE_PATH}" \\
  --form "model=\${MODEL_NAME}"
`,
    nodejs: (
      config,
      apiKeyVar,
      filePathVar,
      inferenceConfig,
      modelName,
    ) => `// npm install node-fetch form-data

import fs from "fs";
import fetch from "node-fetch";
import FormData from "form-data";

const API_KEY = "${apiKeyVar}"; // ex: up_xxxYYYzzzAAAbbbCCC
const FILE_PATH = "${filePathVar}"; // ex: ./image.png
const MODEL_NAME = "${modelName || config.defaultModelName}";  

async function performExtraction() {
  const formData = new FormData();
  formData.append("document", fs.createReadStream(FILE_PATH));
  formData.append("model", MODEL_NAME);

  try {
    const response = await fetch(
      "${config.apiUrl}", 
      {
        method: "POST",
        headers: { Authorization: \`Bearer \${API_KEY}\` },
        body: formData,
      }
    );
    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }
    console.log(await response.json());
  } catch (error) {
    console.error("Error:", error);
  }
}

performExtraction();
`,
  },
};
